<script setup>
import { ActiveSectionKey, useActiveSection } from 'composables/active-section';
import { inject, provide, ref, watch } from 'vue';

const $props = defineProps({
    isNested: {
        type: Boolean,
        default: false
    },
    items: {
        type: Array,
        default: () => []
    }
});

// The outermost list tracks every section on the page and shares it with the lists nested inside it.
const inheritedActiveSection = inject(ActiveSectionKey, null);
const activeSection = inheritedActiveSection ?? useActiveSection(flattenSectionIds($props.items));
const { activeId, followAnchor } = activeSection;

const listElement = ref(null);

if (!inheritedActiveSection) {
    provide(ActiveSectionKey, activeSection);
    watch(activeId, revealActiveLink, { flush: 'post' });
}

function scrollToAnchor(anchorId) {
    const anchorElement = document.getElementById(anchorId);
    if (anchorElement) {
        followAnchor(anchorId);
        anchorElement.scrollIntoView({ behavior: 'smooth' });
        history.pushState(null, null, '#' + anchorId);
    }
}

function linkClasses(item) {
    if (item.id === activeId.value) {
        return 'contents-link contents-link--active block q-py-xs q-pl-sm text-body2';
    }
    return [ 'contents-link block q-py-xs q-pl-sm text-body2', { 'text-weight-medium': !$props.isNested } ];
}

function flattenSectionIds(items) {
    return items.flatMap(item => [ item.id, ...flattenSectionIds(item.children ?? []) ]);
}

// A long list runs past the bottom of the pinned rail. Once the marked link leaves the middle of it, the rail is
// scrolled, never the page, to bring the link back to its upper third.
function revealActiveLink() {
    const link = listElement.value?.querySelector('[aria-current="location"]');
    const rail = link ? findScrollingAncestor(listElement.value) : null;
    if (!rail) {
        return;
    }
    const linkTop = link.getBoundingClientRect().top - rail.getBoundingClientRect().top + rail.scrollTop;
    const margin = rail.clientHeight / 4;
    const isInView = linkTop >= rail.scrollTop + margin
        && linkTop + link.offsetHeight <= rail.scrollTop + rail.clientHeight - margin;
    if (!isInView) {
        rail.scrollTo({ top: Math.max(0, linkTop - rail.clientHeight / 3), behavior: 'smooth' });
    }
}

function findScrollingAncestor(element) {
    let candidate = element.parentElement;
    while (candidate && candidate !== document.body) {
        const { overflowY } = getComputedStyle(candidate);
        if ((overflowY === 'auto' || overflowY === 'scroll') && candidate.scrollHeight > candidate.clientHeight) {
            return candidate;
        }
        candidate = candidate.parentElement;
    }
    return null;
}

defineExpose({ scrollToAnchor });
</script>

<template>
    <ol
        ref="listElement"
        :class="[ 'table-of-contents contents-list q-my-none', $props.isNested ? 'q-pl-md' : 'q-pl-none' ]"
    >
        <li v-for="item in $props.items" :key="item.id">
            <a
                :aria-current="item.id === activeId ? 'location' : undefined"
                :class="linkClasses(item)"
                :href="'#' + item.id"
                @click.prevent="scrollToAnchor(item.id)"
            >{{ item.title }}</a>
            <TableOfContents v-if="item.children" :items="item.children" is-nested/>
        </li>
    </ol>
</template>
